import { Link } from '@tanstack/react-router'
import {
  JournalAccountTotalsTable,
  JournalBalanceNotice,
  JournalEntriesTable,
} from '@/components/accounting/JournalTables'
import { InfoNotice } from '@/components/patterns/info-notice'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Typography } from '@/components/ui/typography'
import { usePathHelper } from '@/hooks/usePathHelper'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import type {
  AccountingJournalReport,
  AccountingJournalUnpostedPayoutReason,
} from '@/lib/services/reports/reports.types'
import { formatCurrency } from '@/lib/utils'
import { ReportCountBadge as CountBadge } from '../-helpers'

type AccountingJournalResultsProps = {
  report: AccountingJournalReport
  /** The `$orgId` route param, parsed to a number. */
  orgId: number
}

/** Why a payout isn't posted yet, from `PayoutJournalSource`. */
const PAYOUT_HOLD_REASONS: Record<AccountingJournalUnpostedPayoutReason, string> = {
  not_reconciled:
    'the payment processor’s list of its transactions doesn’t add up to the payout yet',
  earlier_payout_unavailable: 'it includes an earlier payout we can’t account for',
  not_linked_yet: 'some of its lines haven’t been checked against our records yet',
  awaiting_record: 'one of its lines is waiting for its matching record in OneStem',
  unresolved: 'one of its lines couldn’t be matched to our records',
}

export function AccountingJournalResults({ report, orgId }: AccountingJournalResultsProps) {
  const path = usePathHelper()
  // The deposit pages need the payouts permission; without it a held payout is plain text, not a dead link.
  const canViewPayouts = useAbility().can('view', 'payouts')
  const { summary } = report
  const { timezone } = report.filters

  return (
    <div className="space-y-4">
      <JournalBalanceNotice summary={summary} />

      {report.unposted_payouts.length > 0 && (
        <InfoNotice
          tone="warning"
          title="Payouts on hold"
          body={
            <ul className="space-y-1">
              {report.unposted_payouts.map((payout) => (
                <li key={payout.provider_id}>
                  {canViewPayouts ? (
                    <Typography
                      as={Link}
                      variant="label"
                      tone="interaction"
                      interaction="hoverUnderline"
                      {...path.deposits.show(payout.settlement_id).using({ orgId }).link}
                    >
                      {formatCurrency(payout.amount)} paid{' '}
                      {dayjs(payout.effective_at).tz(timezone).format('MMM D, YYYY')}
                    </Typography>
                  ) : (
                    <Typography as="span" variant="label">
                      {formatCurrency(payout.amount)} paid{' '}
                      {dayjs(payout.effective_at).tz(timezone).format('MMM D, YYYY')}
                    </Typography>
                  )}
                  : {PAYOUT_HOLD_REASONS[payout.reason]}{' '}
                  <Typography as="span" variant="caption" tone="muted">
                    {payout.provider_id}
                  </Typography>
                </li>
              ))}
            </ul>
          }
        >
          We post a payout once every line in it matches our records. Until then, its money stays in
          card payments clearing.
        </InfoNotice>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Account totals</CardTitle>
          <CardDescription>The net change in each account over these dates.</CardDescription>
        </CardHeader>
        <CardContent>
          <JournalAccountTotalsTable accounts={summary.accounts} />
        </CardContent>
      </Card>

      <Card>
        {/* Wraps the badge under the title only when the card itself runs out of room. */}
        <CardHeader className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1 space-y-1.5">
            <CardTitle>Journal</CardTitle>
            <CardDescription>
              Every entry in these dates, oldest first. Each entry&apos;s debits equal its credits.
            </CardDescription>
          </div>
          <CountBadge>
            {summary.entry_count} {summary.entry_count === 1 ? 'entry' : 'entries'}
          </CountBadge>
        </CardHeader>
        <CardContent>
          <JournalEntriesTable
            entries={report.entries}
            timezone={timezone}
            orgId={orgId}
            showOrderLinks
            emptyState={
              <Typography variant="description">No journal entries in these dates.</Typography>
            }
          />
        </CardContent>
      </Card>
    </div>
  )
}
